import AddIcon from '@mui/icons-material/Add';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, ClickAwayListener, InputBase, Paper, Popper, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { GroupSettingsContext } from "../../contexts/GroupSettingsContext";
import { ReactComponent as Sectors } from "../../assets/icons/sector.svg";
import { ReactComponent as Sentence } from "../../assets/icons/sentence.svg";

type FieldType = 'tags' | 'text' | 'metric';

const FIELD_OPTIONS: { type: FieldType; label: string }[] = [
    { type: 'tags', label: 'TAGS' },
    { type: 'text', label: 'TEXT' },
    { type: 'metric', label: 'METRIC' },
];

const useStyles = makeStyles((theme) => ({
    wrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        marginTop: theme.spacing(1),
        padding: '6px 8px',
        border: '2px dashed #cbd5e1',
        borderRadius: 10,
        transition: 'all 0.2s',
        boxSizing: 'border-box',
        '&:hover': {
            borderColor: '#1eb6a8',
            backgroundColor: '#f0fdf9',
        },
    },
    fieldInput: {
        flex: 1,
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px 0 0 8px',
        minHeight: 36,
        minWidth: 0,
        borderRight: 'none',
        padding: '0 12px',
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        '& input': {
            fontFamily: 'Inter',
            fontSize: '0.85rem',
            fontWeight: 500,
            padding: 0,
            '&::placeholder': {
                color: '#94a3b8',
                opacity: 1,
            },
        },
    },
    typeSelector: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        padding: '6px 10px',
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '0 8px 8px 0',
        minHeight: 36,
        cursor: 'pointer',
        transition: 'all 0.2s',
        flexShrink: 0,
        '&:hover': {
            borderColor: '#cbd5e1',
            backgroundColor: '#f8f9fc',
        },
    },
    typeSelectorLabel: {
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: 600,
        color: '#1e293b',
        whiteSpace: 'nowrap',
    },
    dropdown: {
        minWidth: 160,
        padding: '4px 0',
        borderRadius: 8,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['200']}`,
        boxShadow: '0px 4px 16px rgba(16, 24, 40, 0.12)',
    },
    dropdownItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 16px',
        cursor: 'pointer',
        fontFamily: 'Inter',
        fontSize: '0.8rem',
        fontWeight: 600,
        color: '#1e293b',
        transition: 'background 0.15s',
        '&:hover': {
            backgroundColor: '#f0fdf9',
        },
    },
    dropdownItemIcon: {
        width: 18,
        height: 18,
        flexShrink: 0,
    },
    dropdownItemSelected: {
        backgroundColor: '#f8f9fc',
    },
    fieldInputError: {
        borderColor: '#dc2626 !important',
    },
    errorText: {
        fontFamily: 'Inter',
        fontSize: '0.75rem',
        color: '#dc2626',
        marginTop: 2,
        marginLeft: 26,
    },
    addBtn: {
        minWidth: 56,
        height: 36,
        borderRadius: 8,
        background: 'linear-gradient(135deg, #1eb6a8 0%, #18a598 100%)',
        color: '#fff',
        textTransform: 'none',
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: 600,
        flexShrink: 0,
        padding: '6px 16px',
        boxShadow: 'none',
        '&:hover': {
            background: 'linear-gradient(135deg, #18a598 0%, #159085 100%)',
            boxShadow: 'none',
        },
        '&.Mui-disabled': {
            background: '#e2e8f0',
            color: '#94a3b8',
        },
    },
}));

const AddCustomField: React.FC<{
    onNewTag: (name?: string) => void,
    onNewField: (name?: string) => void,
    onNewMetric?: (name?: string) => void,
}> = ({ onNewTag, onNewField, onNewMetric }) => {
    const classes = useStyles();
    const { customFields, tagGroups } = useContext(GroupSettingsContext);
    const [fieldName, setFieldName] = useState('');
    const [selectedType, setSelectedType] = useState<FieldType | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const typeSelectorRef = useRef<HTMLDivElement>(null);

    const isDuplicateName = useMemo(() => {
        const trimmed = fieldName.trim().toLowerCase();
        if (!trimmed) return false;
        return customFields.some(({ name }) => name.trim().toLowerCase() === trimmed)
            || tagGroups.some(({ title }) => title.trim().toLowerCase() === trimmed);
    }, [fieldName, customFields, tagGroups]);

    const handleSelect = useCallback((type: FieldType) => {
        setSelectedType(type);
        setDropdownOpen(false);
    }, []);

    const handleAdd = useCallback(() => {
        if (!selectedType || isDuplicateName) return;
        const name = fieldName.trim() || undefined;
        if (selectedType === 'tags') {
            onNewTag(name);
        } else if (selectedType === 'metric') {
            onNewMetric?.(name);
        } else {
            onNewField(name);
        }
        setSelectedType(null);
        setFieldName('');
    }, [selectedType, fieldName, isDuplicateName, onNewTag, onNewField, onNewMetric]);

    const selectedOption = FIELD_OPTIONS.find(o => o.type === selectedType);

    return (<>
        <Box className={classes.wrapper}>
            <AddIcon sx={{ fontSize: 18, color: '#94a3b8', flexShrink: 0 }} />
            <InputBase
                className={`${classes.fieldInput} ${isDuplicateName ? classes.fieldInputError : ''}`}
                placeholder="Add a field..."
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
            />
            <Box
                ref={typeSelectorRef}
                className={classes.typeSelector}
                onClick={() => setDropdownOpen(prev => !prev)}
            >
                {selectedOption && (
                    <CheckIcon sx={{ fontSize: 14, color: '#1eb6a8' }} />
                )}
                <Typography className={classes.typeSelectorLabel}>
                    {selectedOption ? selectedOption.label : 'Select type'}
                </Typography>
                <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
            </Box>
            <Button
                variant="contained"
                className={classes.addBtn}
                onClick={handleAdd}
                disabled={!selectedType || isDuplicateName}
            >
                {'Add'}
            </Button>
            <Popper open={dropdownOpen} anchorEl={typeSelectorRef.current} placement="bottom-end" sx={{ zIndex: 1500 }}>
                <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
                    <Paper className={classes.dropdown} elevation={0}>
                        {FIELD_OPTIONS.map((option) => (
                            <Box
                                key={option.type}
                                className={`${classes.dropdownItem} ${selectedType === option.type ? classes.dropdownItemSelected : ''}`}
                                onClick={() => handleSelect(option.type)}
                            >
                                {option.type === 'tags' ? (
                                    <Sectors className={classes.dropdownItemIcon} style={{ stroke: '#64748b' }} />
                                ) : option.type === 'metric' ? (
                                    <BarChartRoundedIcon className={classes.dropdownItemIcon} sx={{ color: '#64748b' }} />
                                ) : (
                                    <Sentence className={classes.dropdownItemIcon} style={{ stroke: '#64748b' }} />
                                )}
                                {option.label}
                            </Box>
                        ))}
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box>
        {isDuplicateName && (
            <Typography className={classes.errorText}>
                {'A field or tag group with this name already exists'}
            </Typography>
        )}
    </>);
}

export default AddCustomField;
